import { useMemo, useState } from "react";
import { OutreachTabs } from "@/components/outreach/OutreachTabs";
import { useLocation } from "wouter";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, Upload, Layers, Search, Loader2, ChevronLeft, ChevronRight,
  ArrowUpRight, Trash2, Send, MailX, MessageSquareReply, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  useOutreachLists, useOutreachLeads, useLeadHistory, useOutreachMutations,
  type OutreachLead, type OutreachList, type ImportReport,
} from "@/hooks/useOutreach";

const STATUS_BADGE: Record<OutreachLead["status"], { label: string; cls: string }> = {
  new: { label: "New", cls: "bg-muted text-muted-foreground" },
  replied: { label: "Replied", cls: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800" },
  bounced: { label: "Bounced", cls: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800" },
  promoted: { label: "Promoted", cls: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800" },
};

function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ── Import dialog ─────────────────────────────────────────────────────────

function ImportDialog({ list, open, onOpenChange }: { list: OutreachList; open: boolean; onOpenChange: (v: boolean) => void }) {
  const m = useOutreachMutations();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportReport | null>(null);

  const runPreview = async (f: File) => {
    setFile(f);
    setPreview(null);
    try {
      const report = await m.importFile.mutateAsync({ listId: list.id, file: f, dryRun: true });
      setPreview(report);
    } catch (e: any) {
      toast.error(e.message);
      setFile(null);
    }
  };

  const confirm = async () => {
    if (!file) return;
    try {
      const report = await m.importFile.mutateAsync({ listId: list.id, file });
      toast.success(`${report.added} leads imported into ${list.name}`);
      onOpenChange(false);
      setFile(null);
      setPreview(null);
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setFile(null); setPreview(null); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Import into {list.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className={cn(
            "flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg py-8 cursor-pointer transition-colors",
            file ? "border-primary/40 bg-primary/5" : "border-border hover:border-primary/40"
          )}>
            <Upload className="w-6 h-6 text-muted-foreground" />
            <span className="text-sm">{file ? file.name : "Drop or choose a .xlsx / .csv file"}</span>
            <span className="text-[11px] text-muted-foreground">
              Columns are matched automatically — Company, Owner, Email, City, State, Revenue, etc. Everything else becomes notes.
            </span>
            <input type="file" accept=".xlsx,.csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) runPreview(f); e.target.value = ""; }} />
          </label>

          {m.importFile.isPending && !preview && (
            <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reading file…</p>
          )}

          {preview && (
            <div className="border rounded-lg p-3 space-y-2 text-sm">
              <p className="font-medium">{preview.added} new leads ready to import</p>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <p>{preview.total} rows found ({preview.sheetsParsed.join(", ")})</p>
                {preview.duplicates > 0 && <p>{preview.duplicates} skipped — email already in mass outreach</p>}
                {preview.noEmail > 0 && <p>{preview.noEmail} skipped — no email address</p>}
                {preview.invalidEmail > 0 && <p>{preview.invalidEmail} skipped — invalid email</p>}
                {(preview.genericInbox ?? 0) > 0 && <p>{preview.genericInbox} skipped — generic inbox (info@, jobs@…), not a named person</p>}
                {preview.suppressed > 0 && <p>{preview.suppressed} skipped — on the suppression list</p>}
              </div>
              {preview.sample && preview.sample.length > 0 && (
                <div className="border-t pt-2 space-y-1">
                  {preview.sample.slice(0, 3).map((s, i) => (
                    <p key={i} className="text-xs truncate">
                      <span className="font-medium">{(s as any).companyName}</span>
                      {(s as any).ownerName && <span className="text-muted-foreground"> · {(s as any).ownerName}</span>}
                      {(s as any).email && <span className="text-muted-foreground"> · {(s as any).email}</span>}
                    </p>
                  ))}
                </div>
              )}
              <Button className="w-full" onClick={confirm} disabled={m.importFile.isPending || preview.added === 0}>
                {m.importFile.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                Import {preview.added} leads
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Lead drawer ───────────────────────────────────────────────────────────

function LeadDrawer({ lead, onClose }: { lead: OutreachLead | null; onClose: () => void }) {
  const m = useOutreachMutations();
  const { data: history = [] } = useLeadHistory(lead?.id ?? null);
  const [, setLocation] = useLocation();

  if (!lead) return null;

  const field = (label: string, key: keyof OutreachLead, placeholder = "") => (
    <div>
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      <Input
        className="mt-0.5 h-8 text-sm"
        defaultValue={(lead[key] as string) ?? ""}
        placeholder={placeholder}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v !== ((lead[key] as string) ?? "")) {
            m.updateLead.mutate({ id: lead.id, [key === "company_name" ? "companyName" : key === "owner_name" ? "ownerName" : key === "owner_title" ? "ownerTitle" : key === "revenue_estimate" ? "revenueEstimate" : key]: v || null },
              { onError: (err: any) => toast.error(String(err.message)) });
          }
        }}
      />
    </div>
  );

  const promote = () => {
    m.promoteLead.mutate(lead.id, {
      onSuccess: (r) => {
        toast.success(r.alreadyPromoted ? "Already promoted" : "Promoted to Prospects");
        setLocation(`/prospects/${r.companyId}`);
      },
      onError: (e: any) => toast.error(String(e.message)),
    });
  };

  return (
    <Sheet open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 pr-8">
            <span className="truncate">{lead.company_name}</span>
            <Badge variant="outline" className={cn("text-[10px] shrink-0", STATUS_BADGE[lead.status].cls)}>
              {STATUS_BADGE[lead.status].label}
            </Badge>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-2.5">
            {field("Company", "company_name")}
            {field("Owner", "owner_name")}
            {field("Title", "owner_title")}
            {field("Email", "email")}
            {field("Phone", "phone")}
            {field("Industry", "industry")}
            {field("City", "city")}
            {field("State", "state")}
            {field("Est. revenue", "revenue_estimate", "$5-10M")}
          </div>

          <div>
            <Label className="text-[11px] text-muted-foreground">Notes — the AI drafter reads these to personalize their email</Label>
            <Textarea
              className="mt-0.5 text-sm" rows={6}
              defaultValue={lead.notes ?? ""}
              placeholder="Thesis, services, size, anything you know…"
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (lead.notes ?? "")) m.updateLead.mutate({ id: lead.id, notes: v || null });
              }}
            />
          </div>

          <div className="flex items-center gap-2">
            {lead.status === "promoted" && lead.promoted_company_id ? (
              <Button size="sm" variant="outline" onClick={() => setLocation(`/prospects/${lead.promoted_company_id}`)}>
                <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Open Prospect record
              </Button>
            ) : (
              <Button size="sm" onClick={promote} disabled={m.promoteLead.isPending}
                variant={lead.status === "replied" ? "default" : "outline"}>
                {m.promoteLead.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ArrowUpRight className="w-3.5 h-3.5 mr-1" />}
                Promote to Prospects
              </Button>
            )}
          </div>

          <div>
            <p className="text-sm font-medium mb-1.5">Outreach history</p>
            {history.length === 0 ? (
              <p className="text-xs text-muted-foreground">No sends yet.</p>
            ) : (
              <div className="border rounded-md divide-y">
                {history.map((h) => (
                  <div key={h.id} className="px-3 py-2 text-xs flex items-center gap-2">
                    {h.status === "replied" ? <MessageSquareReply className="w-3.5 h-3.5 text-blue-600 shrink-0 dark:text-blue-400" />
                      : h.status === "bounced" ? <MailX className="w-3.5 h-3.5 text-red-600 shrink-0 dark:text-red-400" />
                      : <Send className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{h.campaign_name}</p>
                      <p className="text-muted-foreground truncate">
                        {h.sent_at ? `${fmtWhen(h.sent_at)}` : "queued"}
                        {h.sent_from && ` · from ${h.sent_from}`}
                      </p>
                    </div>
                    <span className={cn("shrink-0 capitalize",
                      h.status === "replied" ? "text-blue-600 font-medium dark:text-blue-400" :
                      h.status === "bounced" ? "text-red-600 dark:text-red-400" :
                      h.status === "sent" ? "text-green-700 dark:text-green-400" : "text-muted-foreground")}>
                      {h.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function MassOutreach() {
  const { data: lists = [], isLoading: listsLoading } = useOutreachLists();
  const m = useOutreachMutations();
  const [, setLocation] = useLocation();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeList = lists.find((l) => l.id === selectedId) ?? lists[0] ?? null;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const { data: leadPage, isLoading: leadsLoading } = useOutreachLeads(activeList?.id ?? null, { search, status, page });

  const [newListOpen, setNewListOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [drawerLead, setDrawerLead] = useState<OutreachLead | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newName, setNewName] = useState("");
  const [newIndustry, setNewIndustry] = useState("");

  const leads = leadPage?.leads ?? [];
  const total = leadPage?.total ?? 0;
  const pageSize = leadPage?.pageSize ?? 50;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  // Keep the drawer's lead fresh after edits refetch the page.
  const liveDrawerLead = useMemo(
    () => (drawerLead ? leads.find((l) => l.id === drawerLead.id) ?? drawerLead : null),
    [drawerLead, leads]
  );

  const createList = () => {
    if (!newName.trim()) return;
    m.createList.mutate(
      { name: newName.trim(), industry: newIndustry.trim() || undefined },
      {
        onSuccess: (l) => { setSelectedId(l.id); setNewListOpen(false); setNewName(""); setNewIndustry(""); },
        onError: (e: any) => toast.error(String(e.message)),
      }
    );
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkDelete = () => {
    m.bulkDeleteLeads.mutate([...selected], {
      onSuccess: (r) => { toast.success(`${r.deleted} leads removed`); setSelected(new Set()); },
      onError: (e: any) => toast.error(String(e.message)),
    });
  };

  const filterChip = (value: string, label: string) => (
    <button key={value} onClick={() => { setStatus(value); setPage(1); }}
      className={cn("text-xs px-2.5 py-1 rounded-full border transition-colors",
        status === value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted/50")}>
      {label}
    </button>
  );

  return (
    <AppLayout title="Lead lists">
      <div className="space-y-4 page-enter">
        <h1 className="sr-only">Lead lists</h1>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <OutreachTabs active="lists" />
            <p className="text-xs text-muted-foreground">Cold lists below Prospects. Leads graduate into the CRM when they engage.</p>
          </div>
          {activeList && (
            <div className="flex items-center gap-2">
              <Button variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)}>
                <Upload className="w-4 h-4" /> Import file
              </Button>
              <Button className="gap-1.5" onClick={() => setLocation(`/campaigns/new?list=${activeList.id}`)}>
                <Megaphone className="w-4 h-4" /> New campaign from list
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-start">
          {/* Lists rail */}
          <div className="space-y-1.5">
            {listsLoading ? <Skeleton className="h-24" /> : lists.map((l) => (
              <button key={l.id} onClick={() => { setSelectedId(l.id); setPage(1); setSelected(new Set()); }}
                className={cn("w-full text-left border rounded-lg px-3 py-2.5 transition-colors",
                  activeList?.id === l.id ? "border-primary bg-primary/5" : "hover:bg-muted/40")}>
                <p className="font-medium text-sm truncate">{l.name}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {l.lead_count.toLocaleString()} leads
                  {l.industry && ` · ${l.industry}`}
                </p>
                {(l.replied > 0 || l.contacted > 0) && (
                  <p className="text-[11px] mt-0.5">
                    <span className="text-muted-foreground">{l.contacted} contacted</span>
                    {l.replied > 0 && <span className="text-blue-600 font-medium dark:text-blue-400"> · {l.replied} replied</span>}
                    {l.promoted > 0 && <span className="text-green-700 dark:text-green-400"> · {l.promoted} promoted</span>}
                  </p>
                )}
              </button>
            ))}
            <Button variant="outline" size="sm" className="w-full gap-1" onClick={() => setNewListOpen(true)}>
              <Plus className="w-3.5 h-3.5" /> New list
            </Button>
          </div>

          {/* Leads */}
          {!activeList ? (
            <div className="text-center py-16 border rounded-lg">
              <Layers className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="font-medium">No lists yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create a list, then import a spreadsheet of targets into it.</p>
              <Button className="mt-4 gap-1" onClick={() => setNewListOpen(true)}><Plus className="w-4 h-4" /> New list</Button>
            </div>
          ) : (
            <div className="space-y-3">
            <details className="border rounded-lg px-3 py-2">
              <summary className="text-sm font-medium cursor-pointer select-none">
                Drafting notes for this list
                {!activeList.drafting_notes && <span className="text-muted-foreground font-normal"> — none yet (recommended for AI campaigns)</span>}
              </summary>
              <Textarea
                className="mt-2 text-sm" rows={4}
                key={activeList.id}
                defaultValue={activeList.drafting_notes ?? ""}
                placeholder="How to talk to this trade: vocabulary, market dynamics, what these owners care about. Example: TxDOT lettings drive backlog; bonding capacity limits growth; PE rollups have been buying the trade since 2023; owners care about crews staying employed post-sale."
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (activeList.drafting_notes ?? "")) {
                    m.updateList.mutate({ id: activeList.id, draftingNotes: v || null },
                      { onSuccess: () => toast.success("Drafting notes saved — every AI draft from this list will use them") });
                  }
                }}
              />
            </details>
            <div className="border rounded-lg">
              <div className="flex items-center gap-1.5 px-3 pt-3 pb-2 flex-wrap">
                {filterChip("", `All ${activeList.lead_count.toLocaleString()}`)}
                {filterChip("uncontacted", "Not yet contacted")}
                {filterChip("contacted", `Contacted ${activeList.contacted}`)}
                {filterChip("replied", `Replied ${activeList.replied}`)}
                {filterChip("bounced", `Bounced ${activeList.bounced}`)}
                {filterChip("promoted", `Promoted ${activeList.promoted}`)}
                <div className="relative ml-auto">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-muted-foreground" />
                  <Input className="pl-8 h-7 w-56 max-w-full text-xs" placeholder="Search leads…"
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                </div>
              </div>

              {selected.size > 0 && (
                <div className="flex items-center gap-3 px-3 py-2 bg-muted/40 border-y text-sm">
                  <span>{selected.size} selected</span>
                  <Button size="sm" variant="destructive" className="h-7 gap-1" onClick={bulkDelete} disabled={m.bulkDeleteLeads.isPending}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                  <button className="text-xs text-muted-foreground hover:text-foreground ml-auto" onClick={() => setSelected(new Set())}>Clear</button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-muted-foreground text-left border-b">
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2 font-medium">Company</th>
                      <th className="px-3 py-2 font-medium">Owner</th>
                      <th className="px-3 py-2 font-medium">Email</th>
                      <th className="px-3 py-2 font-medium">Location</th>
                      <th className="px-3 py-2 font-medium">Revenue</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Last sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadsLoading && leads.length === 0 ? (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground text-sm">Loading…</td></tr>
                    ) : leads.length === 0 ? (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground text-sm">
                        No leads{search || status ? " match" : " yet — import a file to fill this list"}.
                      </td></tr>
                    ) : leads.map((l) => (
                      <tr key={l.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => setDrawerLead(l)}>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleSelect(l.id)} />
                        </td>
                        <td className="px-3 py-2 font-medium max-w-[220px] truncate">{l.company_name}</td>
                        <td className="px-3 py-2 max-w-[140px] truncate">{l.owner_name || "—"}</td>
                        <td className="px-3 py-2 max-w-[200px] truncate text-muted-foreground">{l.email || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{[l.city, l.state].filter(Boolean).join(", ") || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{l.revenue_estimate || "—"}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={cn("text-[10px]", STATUS_BADGE[l.status].cls)}>{STATUS_BADGE[l.status].label}</Badge>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{fmtWhen(l.last_sent_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pages > 1 && (
                <div className="flex items-center justify-between px-3 py-2 border-t text-xs text-muted-foreground">
                  <span>{total.toLocaleString()} leads · page {page} of {pages}</span>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            </div>
          )}
        </div>
      </div>

      {/* New list dialog */}
      <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New outreach list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input className="mt-1" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Sitework Sellers — TX/OK" autoFocus
                onKeyDown={(e) => e.key === "Enter" && createList()} />
            </div>
            <div>
              <Label className="text-xs">Industry <span className="text-muted-foreground">(optional)</span></Label>
              <Input className="mt-1" value={newIndustry} onChange={(e) => setNewIndustry(e.target.value)}
                placeholder="Sitework / Earthwork / Utility" />
            </div>
            <Button className="w-full" onClick={createList} disabled={!newName.trim() || m.createList.isPending}>
              {m.createList.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Create list
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {activeList && <ImportDialog list={activeList} open={importOpen} onOpenChange={setImportOpen} />}
      <LeadDrawer lead={liveDrawerLead} onClose={() => setDrawerLead(null)} />
    </AppLayout>
  );
}
